import type {
    ActivityPulse,
    ActivityPulseWindow,
    ActivityTrends,
    AuditEventsPaginatedResponse,
    AuditEventsParams,
    CreateMemberPayload,
    Member,
    MemberDetail,
    MembersPaginatedResponse,
    MembersParams,
    OrganizationSettings,
    OrganizationConfiguration,
    OrganizationFeatureFlags,
    IntegrationsResponse,
    PasswordComplexityPolicy,
    RetentionSettings,
    Seats,
    UpdateOrganizationConfigurationRequest,
    UpdateMemberRolesPayload,
} from '@/types/admin-settings';
import type { OrgStorage, SystemOverview } from '@/types/system';
import { triggerBlobDownload } from '@/utils/download';
import { engineClient } from '@/services/engine/engine-client';

const ENDPOINT = '/api/admin/settings';

export class AdminSettingsService {
    public static async getOrgOverview(): Promise<SystemOverview> {
        return await engineClient.get<SystemOverview>(`${ENDPOINT}/overview`);
    }

    public static async getOrgStorage(): Promise<OrgStorage> {
        return await engineClient.get<OrgStorage>(`${ENDPOINT}/storage`);
    }

    public static async getOrganizationSettings(): Promise<OrganizationSettings> {
        return await engineClient.get<OrganizationSettings>(
            `${ENDPOINT}/organization`,
        );
    }

    public static async getOrganizationConfiguration(): Promise<OrganizationConfiguration> {
        return await engineClient.get<OrganizationConfiguration>(
            `${ENDPOINT}/configuration`,
        );
    }

    public static async getFeatureFlags(): Promise<OrganizationFeatureFlags> {
        return await engineClient.get<OrganizationFeatureFlags>(
            `${ENDPOINT}/feature-flags`,
        );
    }

    public static async updateOrganizationConfiguration(
        payload: UpdateOrganizationConfigurationRequest,
    ): Promise<OrganizationConfiguration> {
        return await engineClient.patch<OrganizationConfiguration>(
            `${ENDPOINT}/configuration`,
            payload,
        );
    }

    public static async getMemberDetail(userId: string): Promise<MemberDetail> {
        return await engineClient.get<MemberDetail>(
            `${ENDPOINT}/members/${userId}`,
        );
    }

    public static async getSeats(): Promise<Seats> {
        return await engineClient.get<Seats>(`${ENDPOINT}/members/seats`);
    }

    public static async getRoles(): Promise<string[]> {
        return await engineClient.get<string[]>(`${ENDPOINT}/members/roles`);
    }

    public static async getMembers(
        params?: MembersParams,
    ): Promise<MembersPaginatedResponse> {
        return await engineClient.get<MembersPaginatedResponse>(
            `${ENDPOINT}/members`,
            params as Record<string, unknown>,
        );
    }

    public static async addMember(
        payload: CreateMemberPayload,
    ): Promise<Member> {
        return await engineClient.post<Member>(`${ENDPOINT}/members`, payload);
    }

    public static async updateMemberRoles(
        userId: string,
        grantId: string,
        payload: UpdateMemberRolesPayload,
    ): Promise<void> {
        await engineClient.put(
            `${ENDPOINT}/members/${userId}/grants/${grantId}/roles`,
            payload,
        );
    }

    public static async removeMember(userId: string): Promise<void> {
        await engineClient.delete(`${ENDPOINT}/members/${userId}`);
    }

    public static async grantOrgOwner(userId: string): Promise<void> {
        await engineClient.post(`${ENDPOINT}/members/${userId}/owner`, {});
    }

    public static async revokeOrgOwner(userId: string): Promise<void> {
        await engineClient.delete(`${ENDPOINT}/members/${userId}/owner`);
    }

    public static async grantOrgUserManager(userId: string): Promise<void> {
        await engineClient.post(
            `${ENDPOINT}/members/${userId}/user-manager`,
            {},
        );
    }

    public static async revokeOrgUserManager(userId: string): Promise<void> {
        await engineClient.delete(`${ENDPOINT}/members/${userId}/user-manager`);
    }

    public static async setMemberPassword(
        userId: string,
        password: string,
    ): Promise<void> {
        await engineClient.post(`${ENDPOINT}/members/${userId}/password`, {
            password,
        });
    }

    public static async unlockMember(userId: string): Promise<void> {
        await engineClient.post(`${ENDPOINT}/members/${userId}/unlock`, {});
    }

    public static async getPasswordComplexityPolicy(): Promise<PasswordComplexityPolicy> {
        return await engineClient.get<PasswordComplexityPolicy>(
            `${ENDPOINT}/password-policy`,
        );
    }

    public static async getRetentionSettings(): Promise<RetentionSettings> {
        return await engineClient.get<RetentionSettings>(
            `${ENDPOINT}/retention`,
        );
    }

    public static async getActivityPulse(
        windowDays: ActivityPulseWindow,
    ): Promise<ActivityPulse> {
        return await engineClient.get<ActivityPulse>(
            `${ENDPOINT}/activity-pulse`,
            { windowDays },
        );
    }

    public static async getActivityTrends(
        windowDays: ActivityPulseWindow,
    ): Promise<ActivityTrends> {
        return await engineClient.get<ActivityTrends>(
            `${ENDPOINT}/activity-trends`,
            { windowDays },
        );
    }

    public static async getIntegrations(): Promise<IntegrationsResponse> {
        return await engineClient.get<IntegrationsResponse>(
            `${ENDPOINT}/integrations`,
        );
    }

    public static async getAuditEvents(
        params?: AuditEventsParams,
    ): Promise<AuditEventsPaginatedResponse> {
        return await engineClient.get<AuditEventsPaginatedResponse>(
            `${ENDPOINT}/audit-logs`,
            params as Record<string, unknown>,
        );
    }

    public static async exportAuditEvents(
        params?: AuditEventsParams,
    ): Promise<void> {
        const response = await engineClient.get<string>(
            `${ENDPOINT}/audit-logs/export`,
            params as Record<string, unknown>,
            { transformResponse: [(data: string) => data] },
        );
        const blob = new Blob([response], { type: 'application/json' });
        triggerBlobDownload(blob, `audit_events_${Date.now()}.json`);
    }
}
